// The death scene (GDD §9, M6): the score (the estate, nakedly), the Life
// Ledger beside it (unscored), the banked-everything counterfactual — the
// game's argument made personal — and the estate record (the v2 inheritance
// object). Death is deterministic; this screen is a scheduled scene.

import type { Params } from '@lifespan/engine';
import { deathModel, type Sample, type Session } from '../game/driver.ts';
import { JOB_TITLES } from '../game/jobs.ts';
import { money, moneyDelta } from '../game/format.ts';

const W = 240;
const H = 54;
const PAD = 3;

function MiniSpark({
  samples,
  pick,
  label,
  min,
  max,
}: {
  samples: Sample[];
  pick: (s: Sample) => number;
  label: string;
  min?: number;
  max?: number;
}) {
  if (samples.length < 2) return null;
  const ys = samples.map(pick);
  const y0 = min ?? Math.min(...ys, 0);
  const y1 = max ?? Math.max(...ys, 1);
  const x0 = samples[0]!.age;
  const x1 = samples[samples.length - 1]!.age;
  const step = Math.max(1, Math.floor(samples.length / 160));
  const pts: string[] = [];
  for (let i = 0; i < samples.length; i += step) {
    const s = samples[i]!;
    const x = PAD + ((s.age - x0) / Math.max(1e-9, x1 - x0)) * (W - 2 * PAD);
    const y = H - PAD - ((pick(s) - y0) / Math.max(1e-9, y1 - y0)) * (H - 2 * PAD);
    pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  return (
    <div className="spark-cell">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={label}>
        <polyline points={pts.join(' ')} fill="none" />
      </svg>
      <span>{label}</span>
    </div>
  );
}

export function DeathScreen({
  session,
  P,
  onRestart,
  onExport,
}: {
  session: Session;
  P: Params;
  onRestart: () => void;
  onExport: () => void;
}) {
  const d = deathModel(session, P);
  const cfEstateDelta = d.counterfactual.estate - d.estate;
  const cfYearsDelta = d.counterfactual.deathAge - d.deathAge;
  const spentTotal = d.ledger.lines.living + d.ledger.lines.tuition;

  const downloadRecord = () => {
    const blob = new Blob([JSON.stringify(d.estateRecord, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lifespan-estate-${d.deathAge.toFixed(1)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="panel death-screen" aria-label="End of life">
      <div className="death-head">
        <div className="death-title">† Died at {d.deathAge.toFixed(1)}</div>
        <div className="death-score-label">The estate</div>
        <div className="death-score">{money(d.estate)}</div>
        <div className="death-sub">
          {JOB_TITLES[d.K] ?? `Tier ${d.K}`}
          {d.skillAges.length > 0 &&
            ` · skills at ${d.skillAges.map((a) => a.toFixed(0)).join(', ')}`}
        </div>
      </div>

      <div className="death-cols">
        <div className="death-ledger">
          <div className="panel-title">The Life Ledger</div>
          <div className="line">
            <span>Years flourishing</span>
            <span>{d.ledger.flourishYears.toFixed(1)}</span>
          </div>
          <div className="line">
            <span>Lifetime earned</span>
            <span>{money(d.ledger.earnedTotal)}</span>
          </div>
          <div className="line">
            <span>Lifetime lived on</span>
            <span>{money(d.ledger.livedOnTotal)}</span>
          </div>
          <div className="line">
            <span>Sabbaticals</span>
            <span>
              {d.ledger.sabStretches > 0
                ? `${d.ledger.sabStretches} (${d.ledger.sabYears.toFixed(1)} years)`
                : 'none'}
            </span>
          </div>
          <div className="line">
            <span>Experiences bought</span>
            <span>— (cards land in M7)</span>
          </div>
          <div className="line">
            <span>Peak overdraft</span>
            <span>
              {d.ledger.minCash >= 0
                ? `none (never below ${money(d.ledger.minCash)})`
                : money(d.ledger.minCash)}
            </span>
          </div>
          <div className="line emph">
            <span>Of the estate, compound interest</span>
            <span>
              {money(d.ledger.lines.interest)}
              {/* the share is G12's number only for a positive estate with a
                  sane ratio — never "98% of the debt" under this label */}
              {d.estate > 0 && d.ledger.interestShare >= 0 && d.ledger.interestShare <= 1.0001
                ? ` (${Math.round(d.ledger.interestShare * 100)}%)`
                : ''}
            </span>
          </div>
          <div className="spark-row">
            <MiniSpark samples={session.samples} pick={(s) => s.S} label="cash" />
            <MiniSpark samples={session.samples} pick={(s) => s.M} label="morale" min={0} max={100} />
            <MiniSpark samples={session.samples} pick={(s) => s.H} label="health" min={0} max={100} />
          </div>
        </div>

        <div className="death-cf">
          <div className="panel-title">If every Living dollar had been banked</div>
          <p className="cf-quote">
            Banking everything: <strong>{money(d.counterfactual.estate)}</strong> (
            {cfEstateDelta >= 0 ? '+' : '−'}
            {money(Math.abs(cfEstateDelta))}), died at{' '}
            <strong>{d.counterfactual.deathAge.toFixed(1)}</strong> (
            {cfYearsDelta >= 0 ? '+' : '−'}
            {Math.abs(cfYearsDelta).toFixed(1)} years), reached Skill {d.counterfactual.K}.
          </p>
          {/* which sentence renders is classified in deathModel (d.verdict)
              and pinned by death.test.ts — §9.3's copy is graded, not vibes */}
          {d.verdict === 'investment' ? (
            <p className="cf-verdict">
              Your {money(spentTotal)} of Living and tuition bought{' '}
              <strong>{money(Math.abs(cfEstateDelta))}</strong>
              {cfYearsDelta < -0.05 ? (
                <>
                  {' '}
                  and <strong>{Math.abs(cfYearsDelta).toFixed(1)} years</strong>
                </>
              ) : (
                <> — and cost nothing in lifespan</>
              )}
              . The spending was the investment.
            </p>
          ) : d.verdict === 'respect' ? (
            <p className="cf-verdict">
              Banking even your {money(spentTotal)}: {moneyDelta(cfEstateDelta)}
              {cfYearsDelta < -0.05 && (
                <>, {Math.abs(cfYearsDelta).toFixed(1)} fewer years</>
              )}
              — your spending was correct within this strategy.
            </p>
          ) : d.verdict === 'time' ? (
            <p className="cf-verdict">
              No Living was ever spent — the difference is <em>time</em>: working those
              sabbatical years as Standard would have{' '}
              {cfEstateDelta >= 0 ? 'banked' : 'cost'}{' '}
              <strong>{money(Math.abs(cfEstateDelta))}</strong>
              {Math.abs(cfYearsDelta) >= 0.05 && (
                <>
                  {' '}
                  and {cfYearsDelta < 0 ? 'ended' : 'extended'} the life{' '}
                  {Math.abs(cfYearsDelta).toFixed(1)} years{' '}
                  {cfYearsDelta < 0 ? 'earlier' : 'later'}
                </>
              )}
              .
            </p>
          ) : d.verdict === 'banked' ? (
            <p className="cf-verdict">
              You banked everything — this life <em>was</em> the counterfactual.
            </p>
          ) : (
            <p className="cf-verdict">
              Banking would have finished ahead on the scoreboard — within this strategy,
              the spending was the price of the life, not the engine of it.
            </p>
          )}
          <p className="cf-note">
            Same life, re-simulated: dial at $0, sabbaticals worked as Standard, enrolments
            attempted where you attempted them (and stalling where morale collapsed).
          </p>
        </div>
      </div>

      <div className="death-actions">
        <button onClick={downloadRecord}>Download estate record</button>
        <button onClick={onExport}>Export the life</button>
        <button className="plan-file" onClick={onRestart}>
          Live again
        </button>
      </div>
    </section>
  );
}
